import { Analysis, PossibleRootCauseCategory, ProbabilityCategory } from '.';

export class GlucoseAnalysis extends Analysis {
    glucoseRegulationProbability?: number;
    glucoseRegulationProbabilityCategory?: ProbabilityCategory;

    reset() {
        super.reset();
        this.glucoseRegulationProbability = -1;
        this.glucoseRegulationProbabilityCategory = ProbabilityCategory.NA;
    }

    performAnalysis() {
        super.performAnalysis();
        this.calculateGlucoseRegulationLikelihood();
        this.calculateGlucoseRegulationLikelihoodCategory();
    }

    calculateGlucoseRegulationLikelihood() {
        const ba = this.ba;
        const hasGlucose = ba.glucose?.hasValue?.();
        const hasHbA1c = ba.hemoglobinA1c?.hasValue?.();
        const hasTgHdl = ba.triglyceridesHDLRatio?.hasValue?.();

        if (!hasGlucose && !hasHbA1c && !hasTgHdl) return;

        const gHigh = hasGlucose && ba.glucose.isGreaterThanOptimal();
        const gLab  = hasGlucose && ba.glucose.isGreaterThanLab();
        const hHigh = hasHbA1c  && ba.hemoglobinA1c.isGreaterThanOptimal();
        const hLab  = hasHbA1c  && ba.hemoglobinA1c.isGreaterThanLab();
        const tHigh = hasTgHdl  && ba.triglyceridesHDLRatio.isGreaterThanOptimal();
        const tLab  = hasTgHdl  && ba.triglyceridesHDLRatio.isGreaterThanLab();

        if ((gLab && hLab) || (gLab && tLab) || (hLab && tLab)) {
            this.glucoseRegulationProbability = 95;
        } else if ((gHigh && hHigh) || (gHigh && tHigh) || (hHigh && tHigh) || gLab || hLab) {
            this.glucoseRegulationProbability = 80;
        } else if (gHigh || hHigh || tHigh) {
            this.glucoseRegulationProbability = 60;
        } else {
            this.glucoseRegulationProbability = 25;
        }
    }

    calculateGlucoseRegulationLikelihoodCategory() {
        const p = this.glucoseRegulationProbability;
        if (p === undefined || p < 0) return;
        if (p >= 90) {
            this.glucoseRegulationProbabilityCategory = ProbabilityCategory.HIGHLY_LIKELY;
        } else if (p >= 70) {
            this.glucoseRegulationProbabilityCategory = ProbabilityCategory.LIKELY;
        } else if (p >= 50) {
            this.glucoseRegulationProbabilityCategory = ProbabilityCategory.POSSIBLE;
        } else {
            this.glucoseRegulationProbabilityCategory = ProbabilityCategory.LESS_LIKELY;
        }
    }

    private addGlucosePatternCategory(category: PossibleRootCauseCategory) {
        if (!this.possibleRootCauseCategories.includes(category)) {
            this.possibleRootCauseCategories.push(category);
        }
    }

    private markerHasValue(marker?: any): boolean {
        return !!marker && !!marker.hasValue && marker.hasValue();
    }

    private isMarkerHigh(marker?: any): boolean {
        if (!this.markerHasValue(marker)) return false;
        return marker.isGreaterThanLab?.() || marker.isGreaterThanOptimal?.() || false;
    }

    private isMarkerLow(marker?: any): boolean {
        if (!this.markerHasValue(marker)) return false;
        return marker.isLessThanLab?.() || marker.isStricklyLessThanOptimal?.() || marker.isLessThanOptimal?.() || false;
    }

    private isMarkerOptimal(marker?: any): boolean {
        return this.markerHasValue(marker) && !this.isMarkerHigh(marker) && !this.isMarkerLow(marker);
    }

    calculateInsights() {
        const ba = this.ba;

        // ── Marker presence ────────────────────────────────────────────────────
        const hasGlucose = this.markerHasValue(ba.glucose);
        const hasHbA1c   = this.markerHasValue(ba.hemoglobinA1c);
        const hasInsulin = this.markerHasValue(ba.insulin);
        const hasCp      = this.markerHasValue(ba.cpeptide);

        // ── Glucose / HbA1c states ─────────────────────────────────────────────
        const glucoseHigh = hasGlucose && this.isMarkerHigh(ba.glucose);
        const glucoseLow  = hasGlucose && this.isMarkerLow(ba.glucose);
        const glucoseOpt  = hasGlucose && this.isMarkerOptimal(ba.glucose);
        const hbA1cHigh   = hasHbA1c  && this.isMarkerHigh(ba.hemoglobinA1c);

        // ── Insulin / C-peptide states ─────────────────────────────────────────
        const insulinHigh = hasInsulin && this.isMarkerHigh(ba.insulin);
        const insulinLow  = hasInsulin && this.isMarkerLow(ba.insulin);
        const insulinOpt  = hasInsulin && this.isMarkerOptimal(ba.insulin);
        const cpHigh      = hasCp     && this.isMarkerHigh(ba.cpeptide);
        const cpLow       = hasCp     && this.isMarkerLow(ba.cpeptide);
        const cpOpt       = hasCp     && this.isMarkerOptimal(ba.cpeptide);

        // ── Lipid offset: 0 = none, 50 = TG/HDL↑, 100 = TG↑+TG/HDL not high ──
        const tgHdlHigh  = this.markerHasValue(ba.triglyceridesHDLRatio) && this.isMarkerHigh(ba.triglyceridesHDLRatio);
        const tgHigh     = this.markerHasValue(ba.triglycerides) && this.isMarkerHigh(ba.triglycerides);
        const lipidOffset = tgHdlHigh ? 50 : (tgHigh ? 100 : 0);

        // ── Insulin variant (0–45, step 5) for main high-glucose patterns ──────
        const insulinVariant = (() => {
            if (!hasInsulin && !hasCp) return 0;
            if (hasInsulin && !hasCp) {
                if (insulinHigh) return 5;
                if (insulinOpt)  return 20;
                return 35; // insulinLow
            }
            if (!hasInsulin && hasCp) {
                if (cpHigh) return 10;
                if (cpOpt)  return 25;
                return 40; // cpLow
            }
            // Both present: high takes priority, then low, then both-opt
            if (insulinHigh || cpHigh) return 15;
            if (insulinLow  && cpLow)  return 45;
            return 30; // both optimal (or opt/low mix → treated as both-opt)
        })();

        // ── Main pattern: groupBase + lipidOffset + insulinVariant ─────────────
        if (glucoseHigh || hbA1cHigh) {
            let groupBase: number;
            if      (glucoseHigh && hbA1cHigh)    groupBase = 72800;
            else if (glucoseHigh && !hasHbA1c)    groupBase = 72000;
            else if (glucoseHigh)                  groupBase = 72400; // HbA1c present but ≤ optimal
            else if (hbA1cHigh && !hasGlucose)    groupBase = 72200;
            else                                   groupBase = 72600; // Glucose present but ≤ optimal
            const catId = groupBase + lipidOffset + insulinVariant;
            this.addGlucosePatternCategory((PossibleRootCauseCategory as any)['CATEGORY_' + catId]);
        }

        // ── Low glucose primary pattern (19.x: 73050–73080) ───────────────────
        if (glucoseLow && !hbA1cHigh) {
            const lowVariant = (() => {
                if (!hasInsulin && !hasCp) return 0;
                if (hasInsulin && !hasCp)  return insulinHigh ? 5 : 20; // high or ≤ opt
                if (!hasInsulin && hasCp)  return cpHigh ? 10 : 25;
                // Both present
                if (insulinHigh || cpHigh) return 15;
                return 30; // both ≤ optimal
            })();
            this.addGlucosePatternCategory((PossibleRootCauseCategory as any)['CATEGORY_' + (73050 + lowVariant)]);
        }

        // ── Optimal glucose + high insulin/cp (21.x: 73200–73240) ─────────────
        if (glucoseOpt && !hbA1cHigh && (insulinHigh || cpHigh)) {
            const lipidOffset21    = tgHdlHigh ? 15 : (tgHigh ? 30 : 0);
            const insulinVariant21 = (insulinHigh && cpHigh) ? 10 : (insulinHigh ? 0 : 5);
            this.addGlucosePatternCategory((PossibleRootCauseCategory as any)['CATEGORY_' + (73200 + lipidOffset21 + insulinVariant21)]);
        }

        // ── Add-ons (fire independently of primary pattern) ───────────────────
        const anyHigh = glucoseHigh || hbA1cHigh;

        // 16.x: Atherogenic Lipid Profile
        if (anyHigh && this.isMarkerHigh(ba.ldl)) {
            if (tgHdlHigh)      this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_73000);
            else if (tgHigh)    this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_73005);
        }

        // 17.x: Hepatic Contribution to Insulin Resistance
        if (anyHigh && this.isMarkerHigh(ba.alt)) {
            if (tgHdlHigh)      this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_73010);
            else if (tgHigh)    this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_73015);
        }

        // 18.1 / 18.2: CO2 Low
        if (anyHigh && this.isMarkerLow(ba.carbonDioxide)) {
            this.addGlucosePatternCategory(this.isMarkerHigh(ba.bun)
                ? PossibleRootCauseCategory.CATEGORY_73025
                : PossibleRootCauseCategory.CATEGORY_73020
            );
        }

        // 18.3–18.6: Inadequate Pancreatic Response
        if (anyHigh && hasInsulin && !insulinHigh && hasCp && !cpHigh) {
            const kLow  = this.isMarkerLow(ba.potassium);
            const caLow = this.isMarkerLow(ba.calcium);
            if      (kLow && caLow) this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_73045);
            else if (kLow)          this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_73035);
            else if (caLow)         this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_73040);
            else                    this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_73030);
        }

        // 20.1: Protein-Influenced Low Glucose
        if (glucoseLow && this.isMarkerHigh(ba.bun) &&
            (!this.markerHasValue(ba.creatinine) || !this.isMarkerHigh(ba.creatinine))) {
            this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_73100);
        }

        // 20.2: Low Cortisol-Influenced Glucose Pattern
        if (glucoseLow && (this.isMarkerLow(ba.cortisolAM) || this.isMarkerLow(ba.cortisolPM))) {
            this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_73105);
        }

        // 20.3–20.6: B6 / ALT / AST Low Glucose Add-ons
        if (glucoseLow) {
            const b6Low  = this.markerHasValue(ba.vitaminB6) && this.isMarkerLow(ba.vitaminB6);
            const altLow = this.isMarkerLow(ba.alt);
            const astLow = this.isMarkerLow(ba.ast);
            if      (b6Low)            this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_73110);
            else if (altLow && astLow) this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_73125);
            else if (altLow)           this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_73115);
            else if (astLow)           this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_73120);
        }

        // ── Existing single-marker categories ─────────────────────────────────
        if (this.ba.glucose.isGreaterThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_6000);
        } else if (this.ba.glucose.isStricklyLessThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_6010);
        }

        if (this.ba.hemoglobinA1c.isGreaterThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_6020);
        } else if (this.ba.hemoglobinA1c.isStricklyLessThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_6030);
        }

        if (this.ba.insulin.isGreaterThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_6040);
        } else if (this.ba.insulin.isStricklyLessThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_6050);
        }

        if (this.ba.cpeptide.isGreaterThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_6060);
        } else if (this.ba.cpeptide.isStricklyLessThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_6070);
        }

        if (
          this.ba.triglycerides.isGreaterThanOptimal() &&
          this.ba.triglyceridesHDLRatio.isLessThanOrIsOptimal()
        ) {
          this.possibleRootCauseCategories.push(
            PossibleRootCauseCategory.CATEGORY_6075,
          );
        } else if (
          this.ba.triglycerides.isGreaterThanOptimal() &&
          this.ba.triglyceridesHDLRatio.isGreaterThanOptimal()
        ) {
          this.possibleRootCauseCategories.push(
            PossibleRootCauseCategory.CATEGORY_6076,
          );
        } else if (
          this.ba.triglycerides.isLessThanOrIsOptimal() &&
          this.ba.triglyceridesHDLRatio.isGreaterThanOptimal()
        ) {
          this.possibleRootCauseCategories.push(
            PossibleRootCauseCategory.CATEGORY_6077,
          );
        }

        if (this.ba.amylase.isGreaterThanOptimal()) {
        this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_6080);
        }

        if (this.ba.amylase.isGreaterThanLab()) {
        this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_6085);
        }

        if (this.ba.amylase.isLessThanOptimal()) {
        this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_6090);
        }

        if (this.ba.amylase.isLessThanLab()) {
        this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_6091);
        }

    }
}